/**
 * Integration Catalog
 *
 * Code-level SSOT for record sources: telephony, CRM, the public API.
 * Before this module vendors were enumerated in thirteen places and the lists
 * drifted apart: the website promised Zadarma, which exists nowhere in code;
 * Uspacy lived in code but not in the UI; ingest accepted four vendors no
 * screen knew about.
 *
 * No descriptions and no icons here: copy lives in i18n, icons in the
 * component. The catalog holds only what must be identical everywhere.
 */

export type IntegrationGroup = 'telephony' | 'crm' | 'messaging' | 'direct';

export type ConnectMethod =
  | 'webhook_api_key' // telephony: credentials in the wizard form + inbound webhook
  | 'oauth_marketplace' // bitrix24: app installed from the marketplace
  | 'api_key_settings'; // public_api: key issued under Settings → API keys

/**
 * What authenticates a telephony vendor's inbound webhook besides the
 * installation id in its URL (#1403). The id identifies; it is never the key.
 * - `bearer-header` — the installation's `webhookSecret` in `Authorization`
 *   (Unitalk; Ringostat Webhooks 2.0 allows custom headers);
 * - `url-token` — the same secret as `?token=` in the URL (Stream Telecom: the
 *   vendor sends no custom headers and its support registers the URL);
 * - `ip-allowlist` — the sender address (Binotel, list on by default).
 *
 * The ONE declaration: connectors enforces the secret from it
 * (`verifyTelephonyWebhook`), edge-api mints the secret from it, the wizard
 * shows the address or the header from it.
 */
export type TelephonyWebhookAuth = 'ip-allowlist' | 'bearer-header' | 'url-token';

export interface IntegrationDefinition {
  /** Matches the vendor label at the pipeline entrance unless ingestId is set. */
  id: string;
  /** Brand. Never translated. */
  name: string;
  group: IntegrationGroup;
  /**
   * Whether the wizard has a path that reaches the end, not a description of
   * the mechanism. null — the source exists (its label is accepted) but cannot
   * be connected.
   */
  connect: ConnectMethod | null;
  /** What we claim publicly. The CI gate compares this with the website. */
  status: 'live' | 'beta';
  capabilities: {
    /**
     * Whether setup hands the operator an inbound webhook URL of ours.
     *
     * NOT "supports webhooks" in general: Bitrix24 is false here while the
     * product very much processes Bitrix24 portal-event webhooks — those
     * arrive through the marketplace app, not through a URL the source wizard
     * issues and ConnectionTab displays.
     */
    issuesWebhookUrl: boolean;
    /** Whether bulk import over a period is available. */
    bulkImport: boolean;
    /** Whether the label is accepted as a vendor at the pipeline entrance. */
    ingestVendor: boolean;
    /**
     * Whether this vendor's adapter can mint a playback URL for a call id it
     * did not itself just receive over its own webhook — the capability a
     * `media_only` installation needs to serve as another source's audio when
     * that other source (typically a CRM) registers the call but has no file
     * for it.
     *
     * Duplicated here as a static fact rather than read off
     * `TelephonyVendorEntry.adapter.resolveMediaUrl` in `@aica/connectors`:
     * that package depends on `@aica/core`, not the other way around, so this
     * catalog cannot ask the adapter and must instead be kept in agreement
     * with it by hand — the same way `crm.mediaProvider` already is. Kept
     * honest from the other side: `catalog-registry-agreement.test.ts` in
     * @aica/connectors fails the moment this field and
     * `adapter.resolveMediaUrl` disagree. Today this is `true` for exactly one
     * vendor (Binotel, whose media strategy is `fetch-on-demand`);
     * `direct-sync` and `deferred` vendors hand their URL only in their own
     * webhook and cannot mint one for a call they never saw.
     */
    externalAudioSource: boolean;
    /** Telephony only: see `TelephonyWebhookAuth`. Absent for non-telephony sources. */
    webhookAuth?: TelephonyWebhookAuth;
    /**
     * Longest [from, to] a single bulk import may span, in days.
     * Absent = no caller-facing bound.
     *
     * This describes what the CLIENT can serve in one range, NOT the raw
     * provider limit — the two differ. Binotel's API refuses windows over 24h,
     * yet its CDR client splits any range into 24h chunks internally and pages
     * through them, so a caller has no bound to respect. Stream Telecom's
     * client sends `filter[starttime_from/to]` straight through against an API
     * documented at ≤30 days (`streamtelecom/cdr-client.ts` header), so a
     * longer range leaves the provider's contract with nothing to catch it.
     *
     * Lives here rather than in the connectors registry because all three
     * layers need the same number: the importer screen bounds its date inputs,
     * the endpoint refuses an out-of-range request, and the estimator relies on
     * it to keep the counting pass finite.
     */
    maxImportRangeDays?: number;
  };
  /** Vendor label at the entrance when it differs from id (public_api → 'api'). */
  ingestId?: string;
  /**
   * Where the operator installs our app — present exactly for
   * `connect: 'oauth_marketplace'`: the wizard sends that vendor's card there
   * instead of to its own credential form.
   */
  marketplaceUrl?: string;
  /**
   * CRM-specific implementation facts. Present exactly for `group: 'crm'`.
   *
   * These are not copy and not presentation — they are what must be identical
   * in every layer that touches the vendor, which is the same bar the rest of
   * this catalog is held to. Before they lived here, the same five CRMs were
   * enumerated in eight more places with three different meanings, and the
   * lists had already drifted: a type guard denied `uspacy` while the SPA's own
   * union omitted it and fell back to Bitrix24's URL shape.
   */
  crm?: CrmIntegrationFacts;
}

/** Entity kinds every CRM in the catalog can address by id. */
export type CrmEntityUrlType = 'deal' | 'lead' | 'contact' | 'company';

/**
 * What a CRM vendor's delivery path can do, in the terms the deliver worker
 * needs BEFORE it has a provider.
 *
 * Method-level questions ("can it write a timeline comment?") are answered by
 * `CrmDeliveryProvider.capabilities` — an absent method IS the answer, and a
 * provider is the only thing that can be asked. These are the ones a method
 * cannot express, or that must be known before credentials are resolved and a
 * client is constructed. `delivery-support.test.ts` in @aica/connectors is what
 * keeps the facts and the implementations from disagreeing.
 */
export interface CrmDeliverySupport {
  /** Mapped analysis fields can be written onto the CRM entity. */
  structuredFields: boolean;
  /** Smart Process / custom object delivery is available. */
  smartProcess: boolean;
  /** Delivery must keep its own receipt: the CRM write is not idempotent. */
  deliveryReceipts: boolean;
  /** Entity/activity ids are only valid for records from this same install. */
  localEntityIdsOnly: boolean;
  /** A call result can be delivered without a resolved CRM entity. */
  activityOnlyDelivery: boolean;
  /**
   * A timeline note REPLACES the previous one instead of appending to it.
   *
   * Bitrix24's `crm.timeline.note.save` overwrites, so an approved report takes
   * the blocked one's place and repeating it is the correct write. Uspacy's
   * `PATCH call_event.note` is a read-modify-write that appends, so the same
   * repeat leaves the customer reading the report twice (#742) — and a
   * confirmation instead would erase nothing there, while on Bitrix24 it would
   * erase the report itself. One fact, two opposite right answers; deciding it
   * by vendor name is exactly what this catalog exists to prevent.
   */
  timelineNoteReplaces: boolean;
  /**
   * The deal-state comment (#766) is published to this CRM's timeline, so the
   * organization's own switch for it applies. Independent of
   * `structuredFields`: Uspacy takes the comment and no field writes. The
   * Analysis settings → CRM updates step shows the switch for exactly these
   * vendors.
   */
  dealStateComment: boolean;
  /**
   * A deal's next step can be written into this CRM as a task on the deal or
   * lead (a user's click in Aelo, `routes/deals/crm-task.ts`). The route opens
   * the CRM through a per-vendor implementation keyed by `DealTaskCrmVendorId`,
   * so a vendor flagged here without one does not compile.
   */
  dealTask: boolean;
}

export interface CrmIntegrationFacts {
  /**
   * A token manager exists (OAuth refresh + KV storage). `false` ⇒
   * `createCrmTokenManager` refuses the vendor.
   */
  tokenManager: boolean;
  /**
   * A field service exists. It is a BAG of optional methods — field
   * definitions, org-structure import, deal stages and pipelines, outcome
   * semantics — and a vendor implements the ones it can: `true` says "a
   * service can be opened", not "every method is there". A reader of this flag
   * must check the method it calls (`service.getDepartments?`), as Uspacy's
   * funnel-only service shows (#1603).
   */
  fieldService: boolean;
  /**
   * A media provider exists: fetch the recording FROM the CRM by external id.
   * `false` where the event already carries a media URL, which is why a
   * pipeline must consult this instead of assuming one exists.
   */
  mediaProvider: boolean;
  /**
   * Suffixes of official portal domains, used as an SSRF allowlist before any
   * outbound call.
   *
   * NON-EMPTY: the portal domain must be a strict DNS host name (no port, no IP
   * literal, label rules) ending in one of these, or be allowlisted per org.
   *
   * EMPTY means an allowlist is impossible for this vendor, not forgotten:
   * Bitrix24 ships on-premise installs on customer-owned hosts, so a closed
   * list would lock out legitimate portals. Those are guarded by a URL-parser
   * host check (the value must be exactly the host a request contacts - a port,
   * a public IP or `_` in a label stay valid) plus `validateOutboundUrl`, which
   * refuses private and loopback targets.
   */
  officialDomainSuffixes: readonly string[];
  /**
   * We ship an app that runs INSIDE this CRM — a placement, an iframe or a
   * micro-frontend — so its users reach Aelo without leaving the CRM.
   *
   * A distinct fact from `tokenManager`, even though the two sets happen to
   * coincide today: OAuth tokens can exist for a CRM we integrate with purely
   * server-side. What depends on this one is different — the embedded auth
   * handshake, the onboarding endpoints, and the consent flow those run.
   */
  embeddedApp: boolean;
  /**
   * How the in-CRM app's embedded session travels, `null` without one.
   * `cookie` — the HttpOnly partitioned cookie (Bitrix24). `bearer` — the
   * frame keeps the session id in memory and sends `Authorization: Bearer`:
   * a host that offers a third-party frame no storage access gets no cookie,
   * and a cookie there would be the ambient credential the bearer avoids
   * (Uspacy card tab, `docs/plans/2026-10-29-uspacy-card-tab-session.md` §5).
   * Each credential opens only the sessions of vendors declaring it.
   */
  embeddedSessionCredential: 'cookie' | 'bearer' | null;
  /**
   * Who may claim ownership of the organization the in-CRM app onboards,
   * `null` without an in-CRM app.
   *
   * `first_visitor` — the first user to consent wins the claim (Bitrix24's
   * compare-and-swap). `installer_match` — only a user the CRM itself named as
   * the installer, and only through a route that checked it and opts in
   * explicitly (`allowOnboardingOwnerClaim`): Uspacy compares `users/me.id`
   * with the install callback's `installer_uid` (CLAUDE.md, "Uspacy
   * organization-processing boundary"). Readers ask
   * `onboardingOwnerNeedsInstallerMatch`, which answers `true` for an unknown
   * vendor and for `null`: no claim without an explicit opt-in.
   */
  onboardingOwnerClaim: 'first_visitor' | 'installer_match' | null;
  /**
   * An install of this CRM may be read or written only while the customer's
   * processing instruction for THAT install is `active` (Uspacy; CLAUDE.md,
   * "Uspacy organization-processing boundary"). Paused or withdrawn processing
   * fails closed at every reader and writer.
   *
   * A fact, not a vendor test: each reader asks `requiresProcessingInstruction`
   * rather than comparing with `'uspacy'`, so a CRM that gains such an
   * instruction is gated everywhere by flipping this flag — and then names its
   * contract in `INSTRUCTION_COVERS` (`@aica/database`), which a `Record` over
   * this flag's vendors forces at compile time. The contract itself (versions,
   * purposes) is vendor code and stays with the vendor.
   */
  processingInstruction: boolean;
  /**
   * The deal stage and funnel reads were checked against a live portal. `false`
   * — they follow the vendor's documentation only (Uspacy, #1603), so a failed
   * read, or an answer that names no stage in a known funnel, leaves the record
   * as it was before funnels existed: stage capture skips it, deal state takes
   * the standard methodology. It never stamps a verdict such as
   * `crm_stage_source='unknown'` on a shape nobody has seen. `true` (Bitrix24):
   * a failed read is retried and an empty answer is "no pipeline".
   */
  stageReadVerified: boolean;
  /**
   * A stage id names its own funnel (Bitrix24: `C<N>:` for funnel N > 0, no
   * prefix on funnel 0), so per-stage company rules are matched to the deal's
   * funnel without a portal call. `false` — the ids carry no funnel (Uspacy:
   * plain numbers), and the funnel's stage list must be read to match them.
   */
  stageIdEncodesPipeline: boolean;
  /**
   * How this CRM renders text we write into it (comments, task descriptions).
   * `'bbcode'` — tags in model text would become markup, so it goes through
   * `neutralizeBbcode` (`toCrmText`); `'plain'` — written as is.
   */
  textMarkup: 'bbcode' | 'plain';
  /**
   * A chat built from this CRM's activity can be read back by the activity id
   * (`activity.ID`), so a chat too large for the queue is sent without its
   * text and refetched by the record pipeline (Bitrix24 open lines).
   */
  chatActivityRefetch: boolean;
  /**
   * What this vendor's delivery path supports. Declared here, with the vendor's
   * other facts, rather than in a parallel table in @aica/connectors — that
   * table was a sixth hand-maintained vendor list keyed by the same ids.
   */
  delivery: CrmDeliverySupport;
  /**
   * Link to an entity card in the vendor's own UI, or `null` when the shape is
   * unknown. Callers render nothing on `null` — a guessed URL opens a real page
   * belonging to somebody else, which is what the SPA did for Uspacy records in
   * an org connected to two CRMs.
   */
  buildEntityUrl:
    | ((input: {
        /** Portal host, protocol already stripped. */
        domain: string;
        entityType: CrmEntityUrlType;
        entityId: string;
      }) => string)
    | null;
}

/**
 * A CRM whose delivery path is not implemented.
 *
 * Spelled out rather than defaulted: a vendor that gains a delivery path must
 * say so here, and a vendor that has none says so too. A missing `delivery` used
 * to mean "inherits Bitrix24", which is how a phased rollout produces a fake
 * success.
 */
const NO_CRM_DELIVERY: CrmDeliverySupport = {
  structuredFields: false,
  smartProcess: false,
  deliveryReceipts: false,
  localEntityIdsOnly: false,
  activityOnlyDelivery: false,
  // No note is written at all, so «replaces» would claim a behaviour that does
  // not exist. `false` is the answer that makes an approval publish rather than
  // assume.
  timelineNoteReplaces: false,
  dealStateComment: false,
  dealTask: false,
};

/** AmoCRM renames deals to "leads" in its URL space. */
const AMOCRM_ENTITY_PATH: Record<CrmEntityUrlType, string> = {
  deal: 'leads',
  lead: 'leads',
  contact: 'contacts',
  company: 'companies',
};

/**
 * Карточка приложения Aelo в Marketplace Bitrix24.
 *
 * Код приложения (`auspex_streamline_sl.app_aelo`) ведёт в общий маркетплейс:
 * региональный домен на установку не влияет, `.eu` и `.com` равнозначны — поэтому
 * ссылка одна на все поверхности и не подставляется по локали или домену портала.
 *
 * Живёт здесь, а не литералом в мастере подключения: тот же адрес стоит на сайте
 * и в документации, и следующая правка должна быть одна. До публикации в
 * маркетплейсе здесь был поиск `bitrix24.market/search/?q=aica` — по имени, под
 * которым продукта в маркетплейсе нет, то есть заведомо пустая выдача.
 */
export const BITRIX24_MARKETPLACE_URL =
  'https://www.bitrix24.eu/apps/app/auspex_streamline_sl.app_aelo/';

/** Group order on the wizard screen. */
export const INTEGRATION_GROUP_ORDER: readonly IntegrationGroup[] = [
  'telephony',
  'crm',
  'messaging',
  'direct',
] as const;

/**
 * `as const satisfies` instead of a type annotation: an annotation would erase
 * the literals, and `IngestVendorId` — the union the ingest contract rests on —
 * is derived from them. Without it `vendor` at the pipeline entrance degrades
 * to `string` and a typo in a source label stops being a compile error.
 *
 * Declaration order is display order in the wizard, so verified providers come
 * before beta ones within a group.
 */
export const INTEGRATIONS = [
  {
    id: 'streamtelecom',
    name: 'Stream Telecom',
    group: 'telephony',
    connect: 'webhook_api_key',
    status: 'live',
    capabilities: {
      issuesWebhookUrl: true,
      bulkImport: true,
      ingestVendor: true,
      // Direct-sync: the recording URL rides in the webhook itself
      // (`streamtelecom/webhook-adapter.ts`), so the adapter has no
      // resolveMediaUrl and cannot mint one for a call it did not just see.
      externalAudioSource: false,
      webhookAuth: 'url-token',
      // apidoc.streamtele.com: the cdrlist starttime filter takes a window of at
      // most 30 days, and the client does not chunk. Import used to pass the
      // dates through unchecked, so a decade-long range reached the provider
      // as-is.
      maxImportRangeDays: 30,
    },
  },
  {
    id: 'binotel',
    name: 'Binotel',
    group: 'telephony',
    connect: 'webhook_api_key',
    status: 'live',
    capabilities: {
      issuesWebhookUrl: true,
      bulkImport: true,
      ingestVendor: true,
      // Fetch-on-demand: `resolveMediaUrl` mints a fresh call-record URL by id
      // (`binotel/webhook-adapter.ts`) — the only vendor today that can serve
      // as a `media_only` audio source for another source's records.
      externalAudioSource: true,
      webhookAuth: 'ip-allowlist',
    },
  },
  {
    // bulkImport: false — the CDR client works (supportsCdr: true), but
    // POST /api/v1/crm-import does not accept 'unitalk'. A missed opportunity,
    // not a breakage: enabling it is a separate decision that widens the
    // endpoint's source enum, which now derives from bulkImport here.
    id: 'unitalk',
    name: 'Unitalk',
    group: 'telephony',
    connect: 'webhook_api_key',
    status: 'beta',
    capabilities: {
      issuesWebhookUrl: true,
      bulkImport: false,
      ingestVendor: true,
      // Deferred: the webhook itself carries `link` once ready
      // (`unitalk/webhook-adapter.ts`) — no on-demand mint by id.
      externalAudioSource: false,
      webhookAuth: 'bearer-header',
    },
  },
  {
    // bulkImport: false — the CDR client is declared a stub and THROWS
    // ('Ringostat CDR is not implemented', supportsCdr: false). The import tab
    // used to lead to a job that always failed.
    id: 'ringostat',
    name: 'Ringostat',
    group: 'telephony',
    connect: 'webhook_api_key',
    status: 'beta',
    capabilities: {
      issuesWebhookUrl: true,
      bulkImport: false,
      ingestVendor: true,
      // Direct-sync, same as Stream Telecom: URL rides in the webhook, no
      // resolveMediaUrl (`ringostat/webhook-adapter.ts`).
      externalAudioSource: false,
      webhookAuth: 'bearer-header',
    },
  },
  {
    id: 'bitrix24',
    name: 'Bitrix24',
    group: 'crm',
    connect: 'oauth_marketplace',
    marketplaceUrl: BITRIX24_MARKETPLACE_URL,
    status: 'live',
    capabilities: {
      issuesWebhookUrl: false,
      bulkImport: true,
      ingestVendor: true,
      // A CRM, not a telephony operator — cannot hold the media_only role.
      externalAudioSource: false,
    },
    crm: {
      tokenManager: true,
      fieldService: true,
      mediaProvider: true,
      embeddedApp: true,
      embeddedSessionCredential: 'cookie',
      onboardingOwnerClaim: 'first_visitor',
      processingInstruction: false,
      // Checked on live portals: a failed read is retried, an empty one is
      // "no pipeline". Stage ids are `C<N>:…`; comments render BB-code.
      stageReadVerified: true,
      stageIdEncodesPipeline: true,
      textMarkup: 'bbcode',
      chatActivityRefetch: true,
      // Empty by necessity — see CrmIntegrationFacts: on-premise portals live on
      // customer domains and cannot be enumerated.
      officialDomainSuffixes: [],
      delivery: {
        structuredFields: true,
        smartProcess: true,
        // Receipts, although the pipeline already keys the whole delivery per
        // record: that key is written only after EVERY target succeeded, so a
        // partial failure re-runs this delivery — the one moment a receipt is
        // for. And two of its writes append: `crm.timeline.comment.add` adds a
        // comment per call, and the field merge appends text to a filled field.
        // Without receipts that re-run posted the report and the summary twice.
        // `crm.timeline.note.save` replaces its note — a receipt there is
        // redundant, not harmful, and keeps one rule for the vendor.
        deliveryReceipts: true,
        localEntityIdsOnly: false,
        activityOnlyDelivery: false,
        // `crm.timeline.note.save` overwrites: the approved report replaces the
        // blocked one in place, and posting a confirmation instead would delete
        // the report from the note (#742).
        timelineNoteReplaces: true,
        dealStateComment: true,
        // `crm.activity.todo.add` on the deal or lead.
        dealTask: true,
      },
      buildEntityUrl: ({ domain, entityType, entityId }) =>
        `https://${domain}/crm/${entityType}/details/${entityId}/`,
    },
  },
  {
    // Calls Beta connects through the signed Marketplace placement/link flow,
    // not through the standalone source wizard. Keep the wizard card hidden
    // until the self-service embedded shell exists.
    id: 'uspacy',
    name: 'Uspacy',
    group: 'crm',
    connect: null,
    status: 'beta',
    capabilities: {
      issuesWebhookUrl: false,
      bulkImport: false,
      ingestVendor: true,
      externalAudioSource: false,
    },
    crm: {
      tokenManager: true,
      // The Uspacy field service reads DEAL FUNNELS only (#1603): the Deal Stages
      // tab, per-funnel sales methodology, the deal's funnel for deal state and
      // stage capture, and — from the funnels' closing stages — deal outcomes
      // and the snapshot progression verdict (#2086). It has no field catalog,
      // entity read, departments or stage history, so each reader of this flag
      // gates on the METHOD it needs (`getFieldDefinitions?`, `getDepartments?`,
      // `getStageHistory?`; outcome sync: `syncsDealOutcomes`), never on the
      // flag alone. Field mapping, Smart Objects and org-structure
      // import remain later phases (`delivery.structuredFields` stays false).
      fieldService: true,
      // Not needed and not written: the call event carries `call_record_link`,
      // so intake hands the media URL straight to ingest.
      mediaProvider: false,
      embeddedApp: true,
      embeddedSessionCredential: 'bearer',
      onboardingOwnerClaim: 'installer_match',
      processingInstruction: true,
      // Funnel reads follow the plugin docs, not a live portal (#1603); stage
      // ids are plain numbers; comments and tasks render plain text.
      stageReadVerified: false,
      stageIdEncodesPipeline: false,
      textMarkup: 'plain',
      chatActivityRefetch: false,
      officialDomainSuffixes: [
        '.uspacy.com',
        '.uspacy.ua',
        '.uspacy.eu',
        '.uspacy.com.br',
      ],
      delivery: {
        // Calls Beta: note + one comment on an explicit binding. Structured
        // fields and Smart Objects are later phases
        // (docs/plans/2026-07-30-uspacy-connector.md §14).
        structuredFields: false,
        smartProcess: false,
        // `PATCH call_event.note` is a read-modify-write on a shared field and
        // the comment endpoint has no natural dedup key: a redelivery would
        // append the report twice.
        deliveryReceipts: true,
        // Uspacy call/entity ids are local to the portal. A record ingested from
        // a different CRM carrying a numerically identical id must not be
        // addressed as if it were this portal's.
        localEntityIdsOnly: true,
        // An unbound call still gets its result, in the call note.
        activityOnlyDelivery: true,
        // `appendCallNote` is a read-modify-write that APPENDS, so an approval
        // repeating the report would leave two copies in the same note (#742).
        timelineNoteReplaces: false,
        // The comment is written next to the note (`deal-state/publish-comment.ts`).
        dealStateComment: true,
        // A CRM task bound to the deal or lead (`createUspacyDealTasks`).
        dealTask: true,
      },
      buildEntityUrl: ({ domain, entityType, entityId }) =>
        `https://${domain}/crm/${entityType}/${entityId}`,
    },
  },
  {
    id: 'amocrm',
    name: 'amoCRM',
    group: 'crm',
    connect: null,
    status: 'live',
    capabilities: {
      issuesWebhookUrl: false,
      bulkImport: false,
      ingestVendor: true,
      externalAudioSource: false,
    },
    crm: {
      tokenManager: false,
      fieldService: false,
      mediaProvider: false,
      embeddedApp: false,
      embeddedSessionCredential: null,
      onboardingOwnerClaim: null,
      processingInstruction: false,
      stageReadVerified: false,
      stageIdEncodesPipeline: false,
      textMarkup: 'plain',
      chatActivityRefetch: false,
      officialDomainSuffixes: ['.amocrm.ru', '.amocrm.com'],
      delivery: NO_CRM_DELIVERY,
      buildEntityUrl: ({ domain, entityType, entityId }) =>
        `https://${domain}/${AMOCRM_ENTITY_PATH[entityType]}/detail/${entityId}`,
    },
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    group: 'crm',
    connect: null,
    status: 'live',
    capabilities: {
      issuesWebhookUrl: false,
      bulkImport: false,
      ingestVendor: true,
      externalAudioSource: false,
    },
    crm: {
      tokenManager: false,
      fieldService: false,
      mediaProvider: false,
      embeddedApp: false,
      embeddedSessionCredential: null,
      onboardingOwnerClaim: null,
      processingInstruction: false,
      stageReadVerified: false,
      stageIdEncodesPipeline: false,
      textMarkup: 'plain',
      chatActivityRefetch: false,
      officialDomainSuffixes: [],
      delivery: NO_CRM_DELIVERY,
      // `domain` carries the numeric portal id for HubSpot, not a hostname.
      buildEntityUrl: ({ domain, entityType, entityId }) =>
        `https://app.hubspot.com/contacts/${domain}/${entityType}/${entityId}`,
    },
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    group: 'crm',
    connect: null,
    status: 'live',
    capabilities: {
      issuesWebhookUrl: false,
      bulkImport: false,
      ingestVendor: true,
      externalAudioSource: false,
    },
    crm: {
      tokenManager: false,
      fieldService: false,
      mediaProvider: false,
      embeddedApp: false,
      embeddedSessionCredential: null,
      onboardingOwnerClaim: null,
      processingInstruction: false,
      stageReadVerified: false,
      stageIdEncodesPipeline: false,
      textMarkup: 'plain',
      chatActivityRefetch: false,
      officialDomainSuffixes: [],
      delivery: NO_CRM_DELIVERY,
      buildEntityUrl: ({ domain, entityType, entityId }) =>
        `https://${domain}/lightning/r/${entityType}/${entityId}/view`,
    },
  },
  {
    id: 'telegram',
    name: 'Telegram',
    group: 'messaging',
    connect: null,
    status: 'live',
    capabilities: {
      issuesWebhookUrl: false,
      bulkImport: false,
      ingestVendor: true,
      externalAudioSource: false,
    },
  },
  {
    id: 'public_api',
    name: 'Public API',
    group: 'direct',
    connect: 'api_key_settings',
    status: 'live',
    capabilities: {
      issuesWebhookUrl: false,
      bulkImport: false,
      ingestVendor: true,
      externalAudioSource: false,
    },
    ingestId: 'api',
  },
] as const satisfies readonly IntegrationDefinition[];

type Catalog = (typeof INTEGRATIONS)[number];

/** Entries accepted as a source label at the pipeline entrance. */
type IngestEntry = Extract<Catalog, { capabilities: { ingestVendor: true } }>;

/** Entrance label: `ingestId` when set, otherwise `id`. */
type VendorLabel<T> = T extends { ingestId: infer Label extends string }
  ? Label
  : T extends { id: infer Default extends string }
    ? Default
    : never;

/**
 * Vendor labels accepted at the pipeline entrance — as a TYPE.
 * `IngestWorkerInput` in workflows-api used to keep a hand-written copy of this
 * union; it is derived here now and cannot drift from the catalog.
 */
export type IngestVendorId = VendorLabel<IngestEntry>;

/**
 * Telephony the wizard takes to completion — as a TYPE.
 * `setupConfigSchemas` in edge-api is declared `satisfies
 * Record<TelephonyVendorId, …>`: a telephony added to the catalog without
 * credential fields stops compiling instead of failing on
 * `undefined.safeParse` at runtime.
 */
export type TelephonyVendorId = Extract<
  Catalog,
  { group: 'telephony'; connect: 'webhook_api_key' }
>['id'];

/**
 * Vendors that can own an installation row.
 *
 * NOT derived from `connect`, which answers a different question: whether the
 * SOURCE WIZARD has a path that reaches the end. Uspacy's is `null` — it is
 * installed from the Uspacy Marketplace, not through our wizard — and deriving
 * from `connect` therefore excluded a vendor that demonstrably owns install
 * rows, which `GET /api/v1/sources/active` probes one by one. An org whose only
 * source was Uspacy got "no active source": the banner told them to connect
 * something they had already connected, and the importer tab had no vendor.
 *
 * The true test is whether an install row can exist, and both halves of it are
 * catalog facts already: a telephony we take credentials for, or a CRM we hold
 * OAuth tokens for (tokens live in KV keyed by the install).
 *
 * The public API is neither: it is connected by issuing a key, and
 * `/sources/active` looks it up separately.
 */
export type InstallableVendorId =
  | Extract<Catalog, { group: 'telephony'; connect: 'webhook_api_key' }>['id']
  | CrmVendorId;

/** Vendors we hand an inbound webhook URL to at setup. */
export type WebhookUrlVendorId = Extract<
  Catalog,
  { capabilities: { issuesWebhookUrl: true } }
>['id'];

/** Vendors bulk import can actually run against. */
export type BulkImportVendorId = Extract<
  Catalog,
  { capabilities: { bulkImport: true } }
>['id'];

/**
 * CRMs bulk import can run against — as a TYPE. The scheduled CRM backfill
 * reads activities the same way an import over a period does, and keys its
 * per-vendor handler by this type: a CRM given `bulkImport` without a backfill
 * handler does not compile.
 */
export type CrmBulkImportVendorId = Extract<
  Catalog,
  { group: 'crm'; capabilities: { bulkImport: true } }
>['id'];

/**
 * Vendors that can serve as the audio source for a `media_only` installation
 * — as a TYPE, derived from the catalog.
 */
export type ExternalAudioSourceVendorId = Extract<
  Catalog,
  { capabilities: { externalAudioSource: true } }
>['id'];

/**
 * CRM vendor ids — as a TYPE, derived from the catalog.
 *
 * `CrmProviderType` in `crm/types.ts` used to be a hand-written union of the
 * same five names. It is an alias of this now, so a CRM added to the catalog is
 * a CRM everywhere, and one added to the union without a catalog entry stops
 * compiling.
 */
export type CrmVendorId = Extract<Catalog, { group: 'crm' }>['id'];

/**
 * CRMs we ship an in-CRM app for — as a TYPE.
 *
 * Narrower than `CrmVendorId` on purpose: the embedded auth handshake, the
 * onboarding endpoints and the consent flow accept only these, and a schema
 * typed as "any CRM" would accept a value none of that code can serve. Six
 * hand-written `'bitrix24' | 'uspacy'` pairs used to say this, and each one
 * would have rejected a third embedded CRM with a validation error naming a
 * field rather than a vendor.
 */
export type EmbeddedCrmVendorId = Extract<Catalog, { crm: { embeddedApp: true } }>['id'];

/**
 * CRMs with a field service — as a TYPE, derived from `fieldService: true`.
 *
 * The connectors' registry of service classes is keyed by exactly this type,
 * so the catalog flag and the classes cannot disagree: a vendor flagged here
 * without a class, or a class for a vendor not flagged, does not compile.
 */
export type FieldServiceCrmVendorId = Extract<
  Catalog,
  { crm: { fieldService: true } }
>['id'];

/**
 * CRMs whose installs need an active processing instruction — as a TYPE,
 * derived from `processingInstruction: true`. The processing-state registry is
 * keyed by exactly this type, so a vendor flagged here has a state row to read.
 */
export type ProcessingInstructionCrmVendorId = Extract<
  Catalog,
  { crm: { processingInstruction: true } }
>['id'];

/**
 * CRMs with a token manager — as a TYPE, derived from `tokenManager: true`.
 * Only these hold the OAuth material to talk to a portal, so the source
 * disconnect/reconnect path keys its per-vendor event (un)binding by this type:
 * a CRM given a token manager without one does not compile.
 */
export type TokenManagerCrmVendorId = Extract<
  Catalog,
  { crm: { tokenManager: true } }
>['id'];

/**
 * CRMs a deal task can be written into — as a TYPE, derived from
 * `delivery.dealTask: true`. The task route keys its per-vendor CRM opener by
 * this type.
 */
export type DealTaskCrmVendorId = Extract<
  Catalog,
  { crm: { delivery: { dealTask: true } } }
>['id'];

/** Entries of a group that can actually be connected through the wizard. */
export function getConnectableByGroup(group: IntegrationGroup): IntegrationDefinition[] {
  return INTEGRATIONS.filter((i) => i.group === group && i.connect !== null);
}

/**
 * Vendors probed for an active installation, most significant first.
 *
 * CRMs come FIRST on purpose. An org can hold a CRM app install and a directly
 * connected telephony at the same time — POST /setup only deactivates other
 * telephony, and the CRM install is written by the OAuth flow, which /setup and
 * /disconnect never touch. The CRM is the org-wide integration the customer
 * thinks of as their source, so probing telephony first would silently rename
 * their active source and switch which bulk import runs. Pinned by tests.
 *
 * The filter used to be `connect is 'oauth_marketplace' | 'webhook_api_key'`,
 * which reads as "installable" and is not: `connect` describes the WIZARD, and
 * Uspacy's is `null` because it installs from the Uspacy Marketplace instead.
 * A Uspacy-only org was therefore invisible to `GET /sources/active` — the
 * single signal behind DataSourceBanner and six other consumers — and was told
 * to connect a source it had already connected.
 *
 * A CRM with no token manager is still listed: it cannot have rows today, so
 * probing it costs one miss and nothing else, whereas OMITTING a vendor that
 * does have rows is the failure this comment exists to describe.
 */
export function getInstallableVendorIds(): InstallableVendorId[] {
  const entries = INTEGRATIONS as readonly IntegrationDefinition[];
  return [
    ...entries.filter((i) => i.group === 'crm'),
    ...entries.filter((i) => i.group === 'telephony' && i.connect === 'webhook_api_key'),
  ].map((i) => i.id as InstallableVendorId);
}

/** Telephony the wizard takes to completion. */
export function getTelephonyVendorIds(): TelephonyVendorId[] {
  return (INTEGRATIONS as readonly IntegrationDefinition[])
    .filter((i) => i.group === 'telephony' && i.connect === 'webhook_api_key')
    .map((i) => i.id as TelephonyVendorId);
}

/** Vendors we hand an inbound webhook URL to at setup. */
export function getWebhookUrlVendorIds(): WebhookUrlVendorId[] {
  return (INTEGRATIONS as readonly IntegrationDefinition[])
    .filter((i) => i.capabilities.issuesWebhookUrl)
    .map((i) => i.id as WebhookUrlVendorId);
}

/**
 * Whether setup hands this vendor an inbound webhook URL of ours.
 *
 * Takes a plain string on purpose: both call sites hold a vendor read off the
 * wire, and a membership test against the narrow array would either not compile
 * or need a cast that hides exactly the mismatch this catalog exists to catch.
 */
export function issuesWebhookUrl(vendorId: string): boolean {
  return INTEGRATIONS.some((i) => i.id === vendorId && i.capabilities.issuesWebhookUrl);
}

/**
 * How a telephony vendor's webhook is authenticated (see `TelephonyWebhookAuth`),
 * or `null` for a vendor that is not telephony or not in the catalog. Plain
 * string in, like the helpers below: callers hold a vendor read off the wire.
 */
export function telephonyWebhookAuth(vendorId: string): TelephonyWebhookAuth | null {
  const entry = INTEGRATIONS.find((i) => i.id === vendorId);
  if (!entry) return null;
  return (
    (entry.capabilities as IntegrationDefinition['capabilities']).webhookAuth ?? null
  );
}

/** Whether setup mints a webhook secret for this vendor — every mode but the IP list. */
export function mintsWebhookSecret(vendorId: string): boolean {
  const auth = telephonyWebhookAuth(vendorId);
  return auth === 'bearer-header' || auth === 'url-token';
}

/**
 * Whether this vendor can serve as the audio source for a `media_only`
 * installation — i.e. mint a recording URL for a call id it did not itself
 * just receive over its own webhook. See `capabilities.externalAudioSource`
 * for why this is a hand-kept fact rather than a read of the adapter, and
 * `catalog-registry-agreement.test.ts` for what keeps the two from drifting.
 *
 * Plain string, same reasoning as `issuesWebhookUrl`: callers hold a vendor
 * read off a D1 row or the wire, not a value already narrowed to the type.
 */
export function supportsExternalAudioSource(vendorId: string): boolean {
  return INTEGRATIONS.some(
    (i) => i.id === vendorId && i.capabilities.externalAudioSource
  );
}

/**
 * Whether an audio-only source can supply anything to records from THIS vendor.
 *
 * The mirror of `supportsExternalAudioSource`: that one names the vendor able
 * to hand over a recording, this one names the record source able to receive
 * it. Both halves are needed, and only their conjunction is a working pair.
 *
 * The answer is `crm.mediaProvider`, and not for a taxonomic reason. The
 * external-audio resolver is reached from ONE place — inside the branch of
 * `wait-for-media-step.ts` that has already built a CRM media provider and
 * asked it for a file. A record source without such a provider never reaches
 * that branch: `public_api` and a directly connected telephony bring their own
 * URL, and a CRM whose events carry the recording (Uspacy) makes the factory
 * refuse before it. So an audio-only installation bought for any of them
 * cannot serve a single record, and the setup screen must not offer it.
 */
export function acceptsExternalAudioSource(vendorId: string): boolean {
  return INTEGRATIONS.some(
    (i) => i.id === vendorId && i.group === 'crm' && i.crm?.mediaProvider === true
  );
}

/**
 * Whether this vendor is a CRM, as opposed to a directly connected telephony.
 *
 * Reads the catalog rather than testing "not in the telephony list": the two
 * are not complements — `public_api` is neither — and a vendor added to a third
 * group would otherwise be silently treated as a CRM.
 */
export function isCrmVendor(vendorId: string): boolean {
  return INTEGRATIONS.some((i) => i.id === vendorId && i.group === 'crm');
}

/**
 * Narrows a vendor read off the wire to one bulk import can actually run for.
 * A type guard rather than a boolean so callers stop casting: the old code cast
 * with `as` and let `public_api` through as a value the type called impossible.
 */
export function isBulkImportVendor(vendorId: string): vendorId is BulkImportVendorId {
  return (getBulkImportVendorIds() as readonly string[]).includes(vendorId);
}

/** Vendors bulk import over a period actually works for. */
export function getBulkImportVendorIds(): BulkImportVendorId[] {
  return (INTEGRATIONS as readonly IntegrationDefinition[])
    .filter((i) => i.capabilities.bulkImport)
    .map((i) => i.id as BulkImportVendorId);
}

/**
 * The same list shaped for `z.enum`, which needs a non-empty tuple.
 *
 * Exists so the assertion is written once. Bulk import is requested at four
 * boundaries — the public endpoint, two internal routes and the workflow's own
 * input schema — and every one of them used to keep its own hand-written enum.
 * They disagreed: one accepted `ringostat`, whose CDR client throws by
 * declaration, another accepted `unitalk`, which the catalog marks
 * `bulkImport: false`, and none of the three matched the endpoint.
 */
export function getBulkImportVendorEnum(): [BulkImportVendorId, ...BulkImportVendorId[]] {
  // The catalog is a static literal holding at least one bulk-import vendor,
  // which the compiler cannot see through `filter`.
  return getBulkImportVendorIds() as [BulkImportVendorId, ...BulkImportVendorId[]];
}

/** What a bulk import can be asked to fetch. 'both' is a request, not a kind. */
export type ImportEntityType = 'call' | 'chat';
export type ImportScope = ImportEntityType | 'both';

/**
 * Record kinds a source can hold at all, regardless of how they are collected.
 *
 * Derived from `group` rather than stored per entry: a telephony source is read
 * from its CDR, and a CDR has no chats — that is a property of the group, not a
 * per-vendor decision someone could get wrong when adding the next PBX. Chats
 * exist only where a CRM holds them.
 *
 * This fact used to live nowhere, and BOTH collectors got it wrong in their own
 * way. Bulk import discovered it mid-run by throwing `Telephony import does not
 * support chat` inside a retried step, after the job row already existed — the
 * job stayed at 'pending' and the importer screen spun. The 30-minute backfill
 * cron hardcoded `['call','chat']` and still spawns a chat run per telephony
 * installation, which re-fetches the same calls and advances a chat checkpoint
 * over call data. One fact, read by both.
 */
export function getSourceRecordKinds(vendorId: string): ImportEntityType[] {
  const entry = INTEGRATIONS.find((i) => i.id === vendorId);
  if (!entry) return [];
  return entry.group === 'telephony' ? ['call'] : ['call', 'chat'];
}

/**
 * Record kinds bulk import can fetch for a vendor; empty when import is
 * unavailable at all. The kinds themselves come from `getSourceRecordKinds` —
 * this adds only the "is import offered here" gate.
 */
export function getBulkImportEntityTypes(vendorId: string): ImportEntityType[] {
  const entry = INTEGRATIONS.find((i) => i.id === vendorId);
  if (!entry?.capabilities.bulkImport) return [];
  return getSourceRecordKinds(vendorId);
}

/**
 * Whether a requested import scope is fetchable for the vendor.
 * 'both' demands both kinds — it is not satisfied by a source that has one.
 */
export function supportsBulkImportScope(vendorId: string, scope: ImportScope): boolean {
  const supported = getBulkImportEntityTypes(vendorId);
  return scope === 'both' ? supported.length === 2 : supported.includes(scope);
}

/**
 * Longest range a single import may span, or undefined when unbounded.
 *
 * Widened to the interface before the lookup: `as const` narrows each entry to
 * its own literal shape, so reading an optional capability off the union fails
 * to compile for the entries that omit it.
 */
export function getMaxImportRangeDays(vendorId: string): number | undefined {
  return (INTEGRATIONS as readonly IntegrationDefinition[]).find((i) => i.id === vendorId)
    ?.capabilities.maxImportRangeDays;
}

/** Inclusive day count of a [from, to] range, both ends counted. */
export function importRangeDays(fromDate: string, toDate: string): number {
  const from = Date.parse(`${fromDate.slice(0, 10)}T00:00:00Z`);
  const to = Date.parse(`${toDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return NaN;
  return Math.floor((to - from) / 86_400_000) + 1;
}

/**
 * Why an import range is unacceptable, or null when it is fine.
 *
 * Returns the numbers rather than a message so each caller phrases it in its
 * own voice — the endpoint in an error body, the screen in the customer's
 * language.
 */
export function checkImportRange(
  vendorId: string,
  fromDate: string,
  toDate: string
): {
  reason: 'invalid' | 'inverted' | 'too_long';
  days: number;
  maxDays?: number;
} | null {
  const days = importRangeDays(fromDate, toDate);
  if (Number.isNaN(days)) return { reason: 'invalid', days: NaN };
  if (days < 1) return { reason: 'inverted', days };

  const maxDays = getMaxImportRangeDays(vendorId);
  if (maxDays !== undefined && days > maxDays) {
    return { reason: 'too_long', days, maxDays };
  }
  return null;
}

/** CRMs among {@link getBulkImportVendorIds}, in catalog order. */
export function getCrmBulkImportVendorIds(): CrmBulkImportVendorId[] {
  return getBulkImportVendorIds().filter(isCrmVendor) as CrmBulkImportVendorId[];
}

/** Vendor labels accepted at the pipeline entrance. */
export function getIngestVendorIds(): IngestVendorId[] {
  return (INTEGRATIONS as readonly IntegrationDefinition[])
    .filter((i) => i.capabilities.ingestVendor)
    .map((i) => (i.ingestId ?? i.id) as IngestVendorId);
}

/**
 * The marketplace page the wizard opens for a vendor connected by installing
 * our app there (`connect: 'oauth_marketplace'`); `null` for every other way
 * of connecting and for an unknown id.
 */
export function getMarketplaceInstallUrl(vendorId: string): string | null {
  const entry = (INTEGRATIONS as readonly IntegrationDefinition[]).find(
    (i) => i.id === vendorId
  );
  return entry?.connect === 'oauth_marketplace' ? (entry.marketplaceUrl ?? null) : null;
}

/** Display name. An unknown id comes back unchanged, as it did before. */
export function getIntegrationDisplayName(id: string): string {
  return INTEGRATIONS.find((i) => i.id === id)?.name ?? id;
}
